import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";

export default function CsvPreviewStep({ previewData, loading }) {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Generando vista previa (dry-run) sin escribir en la base de datos...
        </Typography>
      </Box>
    );
  }

  if (!previewData) {
    return null;
  }

  const {
    total = 0,
    clientes_creados = 0,
    consultas_creadas = 0,
    repetidas = 0,
    conflictos = 0,
    errores = 0,
    resultados = [],
  } = previewData;

  const getStatusChip = (estado, mensaje) => {
    switch (estado) {
      case "CLIENTE_CREADO":
        return <Chip label="Se crearían cliente y consulta" color="success" size="small" />;
      case "CONSULTA_AGREGADA":
        return <Chip label="Se agregaría consulta" color="info" size="small" />;
      case "CONSULTA_REPETIDA":
        return <Chip label="Consulta repetida" color="default" size="small" />;
      case "CONFLICTO_PENDIENTE":
        return <Chip label="Conflicto a resolver" color="warning" size="small" />;
      case "ERROR_VALIDACION":
        return <Chip label="Error" color="error" size="small" />;
      default:
        return <Chip label={estado || "Desconocido"} color="default" size="small" />;
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      {/* Contadores resumidos */}
      <Grid container spacing={1.5} alignItems="stretch" sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#f8f9fa", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Total Filas
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {total}
              </Typography>
              {previewData?.filas_validas !== undefined && (
                <Typography variant="caption" color="success.main" display="block">
                  Válidas: {previewData.filas_validas}
                </Typography>
              )}
              {previewData?.filas_invalidas !== undefined && (
                <Typography variant="caption" color="error.main" display="block">
                  Inválidas: {previewData.filas_invalidas}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#e8f5e9", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="success.main">
                Nuevos Clientes
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {clientes_creados}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#e3f2fd", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="info.main">
                Nuevas Consultas
              </Typography>
              <Typography variant="h6" fontWeight={700} color="info.main">
                {consultas_creadas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#fff3e0", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="warning.main">
                Conflictos
              </Typography>
              <Typography variant="h6" fontWeight={700} color="warning.main">
                {conflictos}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#fafafa", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">
                Repetidas
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {repetidas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card variant="outlined" sx={{ textAlign: "center", bgcolor: "#ffebee", height: "100%" }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography variant="caption" color="error.main">
                Errores
              </Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">
                {errores}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla detallada de filas procesadas */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Detalle de Filas ({resultados.length} filas)
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5", width: 60 }}>
                Fila
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Resultado Estimado
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Nombre
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Teléfono
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Consulta
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Observación
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                Detalles / Mensaje
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resultados.map((row) => (
              <TableRow key={row.fila} hover>
                <TableCell>{row.fila}</TableCell>
                <TableCell>{getStatusChip(row.estado, row.mensaje)}</TableCell>
                <TableCell>{row.nombre || "-"}</TableCell>
                <TableCell>{row.telefono || "-"}</TableCell>
                <TableCell>{row.consulta || "-"}</TableCell>
                <TableCell>{row.observacion || "-"}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {row.mensaje}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
